/** Статья: Harness для AI-агентов (источник: blog_harness_ai_agents.md) */

export const HARNESS_BLOG_SLUG = 'harness-ai-agenty-chto-eto-vnedrenie-on-prem-oblako';

const UTM = 'utm_source=blog&utm_medium=organic&utm_campaign=harness_ai_agents_2026';

export const harnessBlogListItem = {
  slug: HARNESS_BLOG_SLUG,
  title:
    'Harness для AI-агентов: что это, on-premise vs облако и что влияет на производительность',
  description:
    'Harness — инфраструктура вокруг AI-агента: tools, runtime, контекст, права и логи. Сравнение on-prem и облака, harness vs no-code workflow (LangChain), что влияет на производительность.',
  image: '/images/news/4546b4cc-7b79-40f3-899a-832b1c14edcb.png',
  date: '1 июля 2026',
  category: 'Блог',
  readTime: '16 мин',
};

export const harnessBlogPost = {
  title:
    'Harness для AI-агентов: что это, on-premise vs облако и что влияет на производительность',
  seoTitle: 'Harness в ИИ: обвязка агента для бизнеса, сравнение инфраструктур | Блог Антология',
  description:
    'Harness — инфраструктура вокруг AI-агента. Эволюция, on-prem vs облако, squeeze no-code workflow vs harness-агенты (Harrison Chase, LangChain).',
  keywords:
    'harness ИИ, harness engineering, AI agent on-premise, обвязка агента, ReAct агент, внедрение ИИ агентов B2B, n8n vs AI агент, LangFlow Flowise harness',
  date: '1 июля 2026',
  category: 'Блог',
  readTime: '16 мин',
  author: 'Сергей Успенский',
  imageAlt: 'Корпоративный AI-агент: harness, on-premise и облачная инфраструктура',
  canonicalPath: `/blog/${HARNESS_BLOG_SLUG}`,
  datePublishedISO: '2026-07-01',
  dateModifiedISO: '2026-07-01',
  keyTakeaways: [
    'Harness в ИИ — это обвязка вокруг агента: инструменты, runtime-цикл, управление контекстом, права, логи и проверки.',
    'Качество системы зависит от harness не меньше, чем от модели — без него агент остаётся демо, а не production.',
    'Для бизнеса критичны выбор on-premise или облака, пропускная способность inference и дисциплина контекста.',
    'No-code workflow (n8n, LangFlow) и harness-агенты — разные классы решений; средняя полоса визуальных графов сужается.',
  ],
  intro:
    'Жили-были мы с AI-агентом: модель сильная, демо красивое, в чате всё летало. А потом агент начал ходить в инструменты без спроса, контекст раздулся, в логах тишина — и стало ясно, что проблема не в GPT, а в пустоте вокруг модели.',
    intro2:
    'Harness — обвязка вокруг агента: лимиты, цикл, права, память, проверки. Не магия модели, а инженерия вокруг неё. Ниже — для кого текст, из чего состоит harness, on-prem vs облако и на что смотреть при внедрении.',
  sections: [
    {
      title: 'Для кого эта статья',
      paragraphs: [
        'COO, IT-директор, руководитель разработки или цифровой трансформации. Если в переговорах звучат агент, harness и on-prem в одном предложении — ниже разбор без маркетингового шика: что покупаете, где размещать и на что смотреть в первую очередь.',
      ],
    },
    {
      title: 'Зачем вообще появился термин harness?',
      paragraphs: [
        'Harness (буквально «упряжь») — метафора из инженерии: модель даёт рассуждение, harness даёт контроль движения — куда можно ходить, что можно трогать, когда остановиться.',
        'В 2025–2026 сообщество и вендоры всё чаще отделяют выбор модели (GPT, Claude, GigaChat, локальная LLM) от инженерии harness — промпты, tools, циклы, память, безопасность, observability.',
        'Практический смысл для закупки: вы платите не только за API токенов, а за контур исполнения. Два подрядчика с одной моделью могут дать разный результат, если у одного продуман harness, а у второго — чат с tool calling.',
      ],
    },
    {
      title: 'Эволюция: от чистой LLM к harness',
      paragraphs: ['Упрощённая лестница — не догма, но помогает договориться в команде:'],
      dataTable: {
        headers: ['Этап', 'Суть', 'Ограничение'],
        rows: [
          ['Чистая LLM', 'Один запрос — один ответ', 'Нет действий в системах'],
          ['LLM + tools', 'Модель вызывает 1–2 инструмента', 'Короткие сценарии'],
          ['ReAct', 'Цикл: рассуждение → действие → наблюдение', 'Нужна дисциплина цикла'],
          ['Chains / scaffolding', 'Заранее заданные цепочки шагов', 'Хрупко при смене задачи'],
          ['Harness', 'Runtime: tools, права, память, логи, стоп-условия', 'Сложное внедрение'],
          [
            'Следующий шаг',
            'Автономные петли с внешней верификацией (Ralph Loop, AutoResearch)',
            'Нужны guardrails и лимиты',
          ],
        ],
      },
    },
    {
      paragraphs: [
        'ReAct (Reason + Act) — базовый паттерн современного агента: модель думает, вызывает tool, читает результат, повторяет. Harness оборачивает ReAct инфраструктурой: песочница, retries, очередь задач, human-in-the-loop.',
        'Границы размыты: Cursor Agent, Claude Code, Coddy, корпоративные стеки на LangChain deepagents — разные уровни зрелости harness поверх одних и тех же идей.',
      ],
    },
    {
      title: 'Из чего состоит современный harness',
      subtitle: '1. Базовые tools (минимальный набор)',
      paragraphs: [
        'В прикладных агентах почти всегда четыре группы инструментов — по ним видно, готов ли контур к реальной работе:',
      ],
      dataTable: {
        headers: ['Tool', 'Зачем'],
        rows: [
          ['Чтение (read_file, cat)', 'Доступ к файлам, регламентам, коду'],
          ['Запись / правка (write_file, edit)', 'Создание артефактов, черновиков, патчей'],
          ['Исполнение (shell, execute)', 'Запуск команд, скриптов, тестов'],
          ['Поиск (grep, glob, search)', 'Навигация по проекту и базам без ручного перебора'],
        ],
      },
    },
    {
      subtitle: '2. Runtime loop',
      paragraphs: [
        'Цикл исполнения задачи: получить цель → спланировать шаг → вызвать tool → зафиксировать observation → проверить критерий готовности → повторить или завершить.',
        'Здесь живут лимиты итераций, таймауты, retries, эскалация человеку. Без runtime loop агент превращается в бесконечный чат.',
      ],
    },
    {
      subtitle: '3. Context management',
      paragraphs: [
        'Самый недооценённый слой. Длинные сессии деградируют: правила из начала диалога теряются, растёт шум, падает качество (context rot / dumb zone).',
        'Техники harness: compaction (сжатие истории), structured notes (MEMORY.md вне окна), sub-agents (тяжёлая работа в изолированном агенте), fresh context per iteration (Ralph Loop).',
      ],
    },
    {
      title: 'Harness-агенты и no-code workflow: позиция Harrison Chase (LangChain)',
      paragraphs: [
        'Harrison Chase, сооснователь LangChain, в посте Not Another Workflow Builder объясняет, почему LangChain не строил визуальный workflow builder, а отдал нишу LangFlow, Flowise, n8n и другим.',
        'Тезис не в том, что n8n завтра исчезнет — n8n, Flowise, LangFlow решают задачу дать нетехническим пользователям собирать LLM-системы визуально. Workflow builder и agent harness — разные классы решений.',
      ],
      dataTable: {
        headers: ['', 'Workflow (n8n, LangFlow, Flowise)', 'Agent + harness'],
        rows: [
          ['Логика', 'Граф: ветвления, параллель, DSL', 'Prompt + tools в цикле'],
          ['Предсказуемость', 'Выше', 'Ниже на шаге, выше на уровне цели'],
          ['Сложность в UI', 'Быстро — паутина узлов', 'Проще UI, сложность в промпте и tools'],
          ['Кто строит', 'Нетехнический пользователь', 'Harness задаёт runtime, права, память'],
        ],
      },
    },
    {
      title: 'The squeeze: откуда давление на no-code workflow',
      paragraphs: ['Chase описывает эффект сжатия: no-code workflow зажаты с двух сторон.'],
      dataTable: {
        headers: ['Уровень сложности', 'Лучший формат по Chase'],
        rows: [
          ['Низкий', 'No-code агент (prompt + tools + harness)'],
          ['Средний', 'No-code workflow — историческая зона n8n / LangFlow'],
          ['Высокий', 'Workflow в коде (LangGraph и аналоги)'],
        ],
      },
    },
    {
      paragraphs: [
        'Снизу: простые агенты с хорошим harness уже достаточно надёжны для задач, которые раньше собирали блоками. Сверху: сложные сценарии в визуальном редакторе становятся неуправляемыми — альтернатива код (LangGraph). Средняя полоса drag-and-drop сужается.',
        'LangChain фокусируется на Deep Agents и harness-профилях (runtime + defaults поверх LangGraph) — слой, который мы называем harness.',
        'Практический вывод для закупки в РФ: n8n / LangFlow / Flowise — для интеграций и прототипов; harness-агент — для длинных автономных задач, on-prem, аудита. Не путать визуальный граф с production harness без runtime, лимитов и observability.',
      ],
    },
    {
      title: 'On-premise vs облако: что выбрать для harness',
      paragraphs: ['Один и тот же harness можно развернуть по-разному. Сравнение для корпоративного контура:'],
      dataTable: {
        headers: ['Критерий', 'Облако (SaaS / GPU cloud)', 'On-premise / private cloud'],
        rows: [
          ['Старт пилота', 'Быстро: API, managed GPU', 'Дольше: серверы, сеть, ИБ'],
          ['Данные', 'У провайдера (по договору)', 'В периметре'],
          ['152-ФЗ / КИИ / ИБ', 'Зависит от провайдера', 'Проще для чувствительных данных'],
          ['Стоимость на старте', 'Opex, pay-as-you-go', 'Capex или аренда железа'],
          ['Масштаб', 'Эластичный', 'Планируемый, запас GPU'],
          ['Обновления harness', 'У вендора', 'На вас или интеграторе'],
          ['Латентность', 'Канал и регион', 'Ниже внутри LAN'],
          ['Типичный сценарий', 'Пилот, внешние API', 'Регламенты, CRM/1С, госсектор'],
        ],
      },
    },
    {
      paragraphs: [
        'Гибрид — рабочая модель 2026: пилот в облаке, production с ПДн и коммерческой тайной — в on-prem. Harness при переезде не переписывают — меняется backend LLM и хостинг, логика tools и политик остаётся.',
      ],
    },
    {
      title: 'Что в первую очередь влияет на производительность',
      items: [
        'Модель inference и железо — throughput и latency LLM задают потолок.',
        'Context management — мусор в окне на каждой итерации бьёт сильнее смены тарифа.',
        'Качество tools и песочницы — лимиты путей, таймауты, virtual mode для тестов.',
        'Параллелизм и очередь задач — флот контейнеров vs один агент на задачу.',
        'Сеть и интеграции — кэш, локальные индексы, RAG рядом с агентом.',
        'Верификация (backpressure) — тесты, линтеры, schema-check; замедляют итерацию, но режут переделки.',
      ],
    },
    {
      paragraphs: [
        'Вывод для закупки: сначала профилируйте контур (модель + контекст + tools), потом масштабируйте железо. Покупка более дорогой LLM без harness обычно не спасает.',
      ],
    },
    {
      title: 'Как измеряют harness: harness-bench-fast и AutoResearch',
      paragraphs: [
        'Бенчмарк harness-bench-fast (ai-forever) оценивает связку harness + модель на реалистичных задачах: файлы, CSV/JSON, pytest, поиск по дереву, MEMORY.md, многошаговые пайплайны. Меряется не голая модель, а пара модель + обвязка.',
        'В deepagents-gigachat показано, что профиль harness для GigaChat даёт заметный прирост к stock deepagents на том же наборе задач — тюнинг harness отдельная инженерная работа, её можно измерять.',
        'AutoResearch Карпатого — агент в цикле меняет train.py, гоняет эксперимент, сравнивает метрику, коммитит или откатывает. Для корпоративного мира те же принципы: фиксированный бюджет шага, evaluator, состояние в git, не в чате.',
      ],
    },
    {
      title: 'Ralph Loop, backpressure и надзорный цикл',
      paragraphs: [
        'Ralph Loop: агент запускается заново на каждой итерации с чистым контекстом, состояние на диске и в git. Одна задача за итерацию, лимит итераций — для долгих ночных прогонов без context rot.',
        'Backpressure — обратное давление через объективные проверки: unit-тесты, типизация, линтер, билд. Итерация не засчитывается, пока проверки не прошли.',
        'Meta Loop — внешний цикл над агентом: LoopBreakerMiddleware, детекция no-progress, compaction до hard limit, cross-model review. Защита от схлопывания LLM в бессмысленные повторы.',
      ],
    },
    {
      title: 'На что смотреть при внедрении harness в бизнес',
      items: [
        'Тип задачи — нужен ли агент с harness, или хватит RAG / чата с tools.',
        'Данные — что можно отдавать модели, что только on-prem.',
        'Tools — белый список действий, песочница, аудит.',
        'Владелец процесса — кто отвечает за эффект в операции, не только ИТ.',
        'Метрика пилота — одна измеримая, не десять демо.',
        'Observability — логи tool calls, трассировка, воспроизводимость.',
        'Human-in-the-loop — где обязательно человек до продакшена.',
        'Эксплуатация — кто обновляет rules, skills, модель после релиза.',
      ],
    },
    {
      title: 'Связанные материалы',
      relatedLinks: [
        {
          label: 'Классификация ИИ-решений: чат, RAG, агент, harness',
          href: '/blog/klassifikaciya-ii-reshenij-chat-rag-agent-harness',
        },
        {
          label: 'Услуги по ИИ-агентам',
          href: '/services#ai-agents',
        },
      ],
    },
  ],
  conclusion:
    'Harness — не модный ярлык, а слой, который отделяет демо от эксплуатации. Если планируете агентов в контуре компании — разберём архитектуру под ваши требования ИБ, бюджет и сценарий (облако, on-prem, гибрид).',
  conclusionCta: {
    label: 'Обсудить внедрение harness и AI-агентов',
    href: `/discuss-ai?${UTM}&utm_content=cta_bottom`,
  },
  faq: [
    {
      question: 'Чем harness отличается от AI-агента?',
      answer:
        'Агент — логика рассуждения и вызова tools. Harness — инфраструктура вокруг: runtime, права, память, логи, стоп-условия, UI/API. Без harness агент остаётся POC.',
    },
    {
      question: 'Можно ли один harness и в облаке, и on-prem?',
      answer:
        'Да. Меняется размещение LLM и данных, архитектура tools и политик сохраняется.',
    },
    {
      question: 'Нужен ли свой GPU?',
      answer:
        'Для пилота с облачным API — нет. Для закрытого контура, кода и больших объёмов — да, или dedicated GPU у провайдера.',
    },
    {
      question: 'Как понять, что harness настроен плохо?',
      answer:
        'Агент зацикливается, игнорирует rules, теряет инструкции к середине сессии, ломает prod без отката. Лечится backpressure, лимитами и meta-надзором.',
    },
    {
      question: 'С чего начать в компании?',
      answer:
        'Один процесс, один harness, одна метрика, короткий пилот 2–4 недели. Масштабировать после цифр, не после презентации.',
    },
    {
      question: 'Harness вытесняет n8n и LangFlow?',
      answer:
        'Не одномоментно. Простые задачи забирают harness-агенты, сложные — code-first workflow. n8n остаётся силён в интеграциях и средней сложности, но зона чистого drag-and-drop сужается.',
    },
  ],
  sources: [
    {
      label: 'LangChain: Not Another Workflow Builder (Harrison Chase)',
      href: 'https://www.langchain.com/blog/not-another-workflow-builder',
    },
    { label: 'coddy.dev', href: 'https://coddy.dev/' },
    {
      label: 'harness-bench-fast',
      href: 'https://github.com/ai-forever/harness-bench-fast',
    },
    {
      label: 'deepagents-gigachat',
      href: 'https://github.com/ai-forever/deepagents-gigachat',
    },
    {
      label: 'karpathy/autoresearch',
      href: 'https://github.com/karpathy/autoresearch',
    },
    {
      label: 'Anthropic: context engineering',
      href: 'https://www.anthropic.com/engineering/effective-context-engineering-for-ai-agents',
    },
    {
      label: 'Ralph Loop',
      href: 'https://ralphloop.sh/blog/what-is-the-ralph-technique/',
    },
  ],
  structuredData: [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline:
        'Harness для AI-агентов: что это, on-premise vs облако и что влияет на производительность',
      description:
        'Harness — инфраструктура вокруг AI-агента. On-prem vs облако, сравнение с no-code workflow, производительность и внедрение в B2B.',
      author: { '@type': 'Person', name: 'Сергей Успенский' },
      publisher: {
        '@type': 'Organization',
        name: 'Антология',
        logo: { '@type': 'ImageObject', url: 'https://antologia.tech/logo.png' },
      },
      datePublished: '2026-07-01',
      dateModified: '2026-07-01',
      keywords:
        'harness ИИ, AI agent on-premise, harness engineering, внедрение ИИ агентов B2B',
      articleSection: 'Блог',
      inLanguage: 'ru-RU',
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `https://antologia.tech/blog/${HARNESS_BLOG_SLUG}`,
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'Чем harness отличается от AI-агента?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Агент — логика и tools. Harness — runtime, права, память, логи и стоп-условия вокруг.',
          },
        },
        {
          '@type': 'Question',
          name: 'Можно ли один harness и в облаке, и on-prem?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Да. Меняется размещение LLM, архитектура tools сохраняется.',
          },
        },
        {
          '@type': 'Question',
          name: 'Нужен ли свой GPU?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Для облачного пилота — нет. Для закрытого контура — да или dedicated GPU у провайдера.',
          },
        },
      ],
    },
  ],
  checklistPath: '/ready-for-ai',
  showSources: true,
  useDiscussAiCta: true,
};
